import { useEffect, useState } from 'react';
import axios from 'axios';
import { MapPin, Calendar, ArrowUpRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TourPage() {
    const [tourDates, setTourDates] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchTourDates = async () => {
            try {
                const response = await axios.get(`${API}/tour-dates`);
                setTourDates(response.data);
            } catch (error) {
                console.error('Error fetching tour dates:', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchTourDates();
    }, []);

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return {
            day: date.getDate(),
            month: date.toLocaleDateString('en-US', { month: 'short' }),
            year: date.getFullYear(),
            weekday: date.toLocaleDateString('en-US', { weekday: 'short' })
        };
    };

    return (
        <div data-testid="tour-page" className="min-h-screen bg-brand-bg pt-24">
            {/* Header */}
            <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24">
                <div className="max-w-7xl mx-auto">
                    <p className="font-mono text-xs text-brand-primary uppercase tracking-widest mb-4">ON THE ROAD</p>
                    <h1 className="font-syne font-extrabold text-5xl md:text-7xl text-white tracking-tighter uppercase mb-6">
                        TOUR
                    </h1>
                    <p className="font-space text-slate-400 text-lg max-w-2xl">
                        Catch Faded Keenan live. Experience the music in person.
                    </p>
                </div>
            </section>

            {/* Tour Dates */}
            <section className="pb-24 px-6 md:px-12 lg:px-24">
                <div className="max-w-7xl mx-auto">
                    {isLoading ? (
                        <div className="space-y-4">
                            {[1, 2, 3, 4].map((i) => (
                                <Skeleton key={i} className="h-24 bg-brand-surface" />
                            ))}
                        </div>
                    ) : tourDates.length > 0 ? (
                        <div data-testid="tour-dates-list" className="space-y-4">
                            {tourDates.map((date) => {
                                const formattedDate = formatDate(date.date);
                                return (
                                    <div 
                                        key={date.id}
                                        data-testid={`tour-date-item-${date.id}`}
                                        className="group flex flex-col md:flex-row md:items-center justify-between p-6 md:p-8 bg-brand-surface border border-white/5 hover:border-white/20 transition-colors duration-500"
                                    >
                                        {/* Date */}
                                        <div className="flex items-center gap-6 md:gap-8 mb-4 md:mb-0">
                                            <div className="text-center min-w-[80px] border-r border-brand-border pr-6 md:pr-8">
                                                <p className="font-mono text-xs text-slate-500 uppercase mb-1">
                                                    {formattedDate.weekday}
                                                </p>
                                                <p className="font-syne font-extrabold text-4xl text-white">
                                                    {formattedDate.day}
                                                </p>
                                                <p className="font-mono text-xs text-brand-primary uppercase">
                                                    {formattedDate.month}
                                                </p>
                                            </div>

                                            {/* Venue Info */}
                                            <div>
                                                <h3 className="font-syne font-bold text-white text-xl md:text-2xl mb-2">
                                                    {date.venue}
                                                </h3>
                                                <p className="font-space text-slate-400 flex items-center gap-2">
                                                    <MapPin className="w-4 h-4" /> {date.city}
                                                </p>
                                            </div>
                                        </div>

                                        {/* CTA */}
                                        <div className="flex items-center gap-4">
                                            {date.sold_out ? (
                                                <Badge 
                                                    className="bg-white/10 text-slate-400 uppercase tracking-widest px-6 py-2 rounded-none"
                                                >
                                                    Sold Out
                                                </Badge>
                                            ) : (
                                                <a 
                                                    href={date.ticket_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    data-testid={`buy-tickets-${date.id}`}
                                                >
                                                    <Button 
                                                        className="bg-white text-black hover:bg-slate-200 rounded-none px-8 py-4 font-bold uppercase tracking-widest flex items-center gap-2"
                                                    >
                                                        TICKETS <ArrowUpRight className="w-4 h-4" />
                                                    </Button>
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-center py-16">
                            <Calendar className="w-16 h-16 text-slate-600 mx-auto mb-6" />
                            <p className="font-space text-slate-400 text-lg">No tour dates announced yet.</p>
                            <p className="font-space text-slate-500 mt-2">
                                Follow <a href="https://instagram.com/faded.keenan" target="_blank" rel="noopener noreferrer" className="text-brand-primary hover:underline">@faded.keenan</a> for updates.
                            </p>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
